"use client";

import { useState } from "react";
import Link from "next/link";
import { SafeImage } from "@/components/ui/safe-image";
import type { Product } from "@/types";
import type { RecentProduct } from "@/store/recently-viewed.store";
import { formatCurrency, discountPercent, mediaUrl } from "@/lib/utils";
import { QuickAddButton } from "@/components/product/quick-add-button";
import { WishlistButton } from "@/components/product/wishlist-button";
import { FlyToCart } from "@/components/product/fly-to-cart";
import { useDealerStatus } from "@/hooks/use-dealer-status";

const BLUR_DATA_URL = "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";

interface ProductCardProps {
  product: Product | RecentProduct;
  index?: number;
}

// Marketplace kart: sıkışık, fırsat odaklı — büyük indirim rozeti + fiyat vurgusu.
export function CompactProductCard({ product, index = 99 }: ProductCardProps) {
  const [isFlying, setIsFlying] = useState(false);
  const [flyStartRect, setFlyStartRect] = useState<DOMRect | undefined>(undefined);
  const discount = discountPercent(product.price, product.compareAtPrice);
  const isOutOfStock = product.trackStock && product.stock <= 0;

  const { isApproved, discountPercentage } = useDealerStatus();
  const dealerPrice = isApproved
    ? (product.dealerPrice ?? (discountPercentage > 0 ? product.price * (1 - discountPercentage / 100) : null))
    : null;

  const handleAddToCart = (btn: HTMLButtonElement) => {
    setFlyStartRect(btn.getBoundingClientRect());
    setIsFlying(true);
  };

  return (
    <>
      <FlyToCart
        isFlying={isFlying}
        startRect={flyStartRect}
        productImageUrl={mediaUrl(product.coverImageUrl)}
        onDone={() => setIsFlying(false)}
      />
      <Link href={`/products/${product.slug}`} className="group block h-full">
        <div className="flex h-full flex-col overflow-hidden rounded-[var(--sf-radius)] border border-[var(--sf-card-border)] bg-[var(--sf-card-bg)] transition-colors hover:border-[var(--sf-card-hover-border)]">
          <div className="relative aspect-square overflow-hidden bg-gray-50">
            <SafeImage
              src={product.coverImageUrl ? mediaUrl(product.coverImageUrl) : undefined}
              alt={product.name}
              fill
              priority={index < 3}
              placeholder="blur"
              blurDataURL={BLUR_DATA_URL}
              className="object-cover card-image transition-transform duration-500 group-hover:scale-[1.04]"
              style={{ objectPosition: `${product.coverFocalX ?? 50}% ${product.coverFocalY ?? 50}%` }}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
              fallbackClassName="bg-gray-50 text-gray-200"
            />

            {discount > 0 && (
              <span className="absolute left-2 top-2 z-10 rounded-[var(--sf-radius)] px-1.5 py-0.5 text-xs font-bold" style={{ backgroundColor: "var(--sf-badge-sale-bg)", color: "var(--sf-badge-sale-text)" }}>
                -%{discount}
              </span>
            )}

            {isOutOfStock && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                <span className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-widest text-gray-900">
                  Tükendi
                </span>
              </div>
            )}

            <div className="absolute right-2 top-2 z-10 opacity-0 transition-opacity group-hover:opacity-100">
              <WishlistButton
                productId={product.id}
                name={product.name}
                slug={product.slug}
                price={product.price}
                coverImageUrl={product.coverImageUrl}
                size="sm"
                className="h-7 w-7"
              />
            </div>

            <QuickAddButton product={product} onAdd={handleAddToCart} />
          </div>

          <div className="flex flex-1 flex-col p-2.5">
            {product.brandName && (
              <p className="mb-0.5 truncate text-[10px] font-semibold uppercase tracking-wide text-gray-400">{product.brandName}</p>
            )}
            <h3 className="mb-1.5 line-clamp-2 min-h-[2.25rem] text-xs leading-snug text-[var(--sf-card-title)]">{product.name}</h3>
            {discount > 0 && (
              <span className="mb-1 w-fit rounded-[var(--sf-radius)] px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: "var(--sf-accent)", color: "var(--sf-card-btn-text)" }}>
                Sepette -%{discount}
              </span>
            )}
            <div className="mt-auto flex flex-wrap items-baseline gap-x-1.5">
              {dealerPrice ? (
                <>
                  <span className="text-[11px] text-gray-400 line-through">{formatCurrency(product.price)}</span>
                  <span className="text-base font-bold text-[var(--sf-card-price)]">{formatCurrency(dealerPrice)}</span>
                </>
              ) : (
                <span className={`text-base font-bold ${isOutOfStock ? "text-gray-400" : "text-[var(--sf-card-price)]"}`}>
                  {formatCurrency(product.price)}
                </span>
              )}
              {product.compareAtPrice && product.compareAtPrice > product.price && !dealerPrice && (
                <span className="text-[11px] text-gray-400 line-through">{formatCurrency(product.compareAtPrice)}</span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </>
  );
}
